import { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  Building2, LayoutDashboard, Users, UserCog, UserCheck, Target, 
  Settings, LogOut, Menu, X, Brain, BarChart3, ChevronDown, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import GlobalFilters from '@/components/GlobalFilters';
import BetaBanner from '@/components/BetaBanner';
import FoundingWelcome from '@/components/FoundingWelcome';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClubLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clubId } = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [club, setClub] = useState(null);
  const [user, setUser] = useState(null);
  const [filters, setFilters] = useState({
    season: '', team_id: '', category: '', date_from: '', date_to: ''
  });
  const { logout } = useAuth();

  useEffect(() => {
    const token = localStorage.getItem('club_token');
    const storedUser = localStorage.getItem('club_user');
    const storedClub = localStorage.getItem('club_info');
    
    if (!token) {
      navigate('/unified-login');
      return;
    }
    
    if (storedUser) setUser(JSON.parse(storedUser));
    if (storedClub) setClub(JSON.parse(storedClub));
    
    // Fetch fresh club data
    fetchClubData(token);
  }, [navigate]);

  const fetchClubData = async (token) => {
    try {
      const response = await axios.get(`${API}/clubs/my-club`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClub(response.data);
      localStorage.setItem('club_info', JSON.stringify(response.data));
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleLogout = () => {
    // Centralized logout clears all role-scoped tokens to prevent PWA
    // auto-redirect from picking up stale entries from a previous session.
    logout();
    navigate('/unified-login');
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: `/club/${clubId}`, exact: true },
    { icon: BarChart3, label: 'Intelligence', path: `/club/${clubId}/intelligence` },
    { icon: FileText, label: 'Match Reports', path: `/club/${clubId}/match-history` },
    { icon: Users, label: 'Teams', path: `/club/${clubId}/teams` },
    { icon: UserCog, label: 'Coaches', path: `/club/${clubId}/coaches` },
    { icon: UserCheck, label: 'Managers', path: `/club/${clubId}/managers` },
    { icon: Target, label: 'Athletes', path: `/club/${clubId}/athletes` },
    { icon: Settings, label: 'Settings', path: `/club/${clubId}/settings` },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col w-64 border-r border-border/40 bg-card/30">
        {/* Club Header */}
        <div className="p-4 border-b border-border/40">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-sm flex items-center justify-center">
              <Building2 className="text-emerald-400" size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{club?.name || 'Loading...'}</p>
              <p className="text-xs text-muted-foreground">Performance Intelligence</p>
            </div>
          </div>
          <div className="mt-3">
            <BetaBanner variant="inline" />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                isActive(item.path, item.exact)
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'text-muted-foreground hover:bg-card hover:text-white'
              }`}
            >
              <item.icon size={18} />
              <span className="text-sm">{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* User Footer */}
        <div className="p-4 border-t border-border/40">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center">
              <span className="text-xs font-bold text-emerald-400">
                {user?.full_name?.charAt(0) || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="w-full justify-start text-muted-foreground hover:text-red-400"
          >
            <LogOut size={16} className="mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur border-b border-border/40">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-emerald-500/20 rounded-sm flex items-center justify-center">
              <Building2 className="text-emerald-400" size={16} />
            </div>
            <span className="font-semibold text-sm truncate max-w-[150px]">{club?.name}</span>
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-card rounded-sm"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar — flex layout keeps logout reachable even on short PWA viewports */}
      <aside className={`lg:hidden fixed top-0 left-0 bottom-0 w-64 bg-card z-50 transform transition-transform flex flex-col ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-4 border-b border-border/40 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-sm flex items-center justify-center">
              <Building2 className="text-emerald-400" size={20} />
            </div>
            <div>
              <p className="font-semibold text-sm">{club?.name}</p>
              <p className="text-xs text-muted-foreground">Club Admin</p>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="p-1">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                isActive(item.path, item.exact)
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'text-muted-foreground hover:bg-card hover:text-white'
              }`}
            >
              <item.icon size={18} />
              <span className="text-sm">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-border/40 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="w-full justify-start text-muted-foreground hover:text-red-400"
            data-testid="club-mobile-logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:pt-0 pt-16 overflow-auto">
        <div className="p-4 md:p-6 lg:p-8">
          {/* Global Filters - show on dashboard, intelligence, match-history */}
          {(location.pathname === `/club/${clubId}` ||
            location.pathname.startsWith(`/club/${clubId}/intelligence`) ||
            location.pathname.startsWith(`/club/${clubId}/match-history`)) && (
            <GlobalFilters filters={filters} setFilters={setFilters} clubId={clubId} />
          )}
          <Outlet context={{ club, user, filters, setFilters, refreshClub: () => fetchClubData(localStorage.getItem('club_token')) }} />
        </div>
      </main>
      <FoundingWelcome userId={user?.id} role="club_admin" />
    </div>
  );
};

export default ClubLayout;
